import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Languages, Plus, X, Upload, Image as ImageIcon, Video as VideoIcon, Download } from 'lucide-react';
import axios from 'axios';

import { API, API_URL } from '@/config';

// Translation helper for admin texts
const adminTexts = {
  addQuestion: { en: 'Add New Question', ar: 'إضافة سؤال جديد', sv: 'Lägg till ny fråga', tr: 'Yeni Soru Ekle', fa: 'افزودن سوال جدید', so: "Ku dar su'aal cusub", pl: 'Dodaj nowe pytanie', es: 'Agregar nueva pregunta', de: 'Neue Frage hinzufügen' },
  editQuestion: { en: 'Edit Question', ar: 'تعديل السؤال', sv: 'Redigera fråga', tr: 'Soruyu Düzenle', fa: 'ویرایش سوال', so: "Wax ka beddel su'aasha", pl: 'Edytuj pytanie', es: 'Editar pregunta', de: 'Frage bearbeiten' },
  autoTranslate: { en: '✨ Question will be automatically translated to all languages', ar: '✨ سيتم ترجمة السؤال تلقائياً إلى جميع اللغات المدعومة', sv: '✨ Frågan kommer automatiskt att översättas till alla språk', tr: '✨ Soru otomatik olarak tüm dillere çevrilecek', fa: '✨ سوال به طور خودکار به تمام زبان‌ها ترجمه می‌شود', so: "✨ Su'aasha waxaa loo tarjumi doonaa dhammaan luuqadaha", pl: '✨ Pytanie zostanie automatycznie przetłumaczone na wszystkie języki', es: '✨ La pregunta se traducirá automáticamente a todos los idiomas', de: '✨ Die Frage wird automatisch in alle Sprachen übersetzt' },
  selectTest: { en: '📝 Select Test *', ar: '📝 اختر الاختبار *', sv: '📝 Välj test *', tr: '📝 Test Seçin *', fa: '📝 آزمون را انتخاب کنید *', so: '📝 Dooro imtixaanka *', pl: '📝 Wybierz test *', es: '📝 Seleccionar prueba *', de: '📝 Test auswählen *' },
  randomTest: { en: '🎲 Random Test (Auto)', ar: '🎲 اختبار عشوائي (تلقائي)', sv: '🎲 Slumpmässigt test', tr: '🎲 Rastgele Test', fa: '🎲 آزمون تصادفی', so: '🎲 Imtixaan random', pl: '🎲 Losowy test', es: '🎲 Prueba aleatoria', de: '🎲 Zufälliger Test' },
  selectTestPlaceholder: { en: 'Select test to add question to...', ar: 'اختر الاختبار الذي تريد إضافة السؤال إليه...', sv: 'Välj testet att lägga till frågan i...', tr: 'Soru eklenecek testi seçin...', fa: 'آزمون را برای افزودن سوال انتخاب کنید...', so: "Dooro imtixaanka aad su'aasha ku dari rabto...", pl: 'Wybierz test, do którego chcesz dodać pytanie...', es: 'Seleccione la prueba a la que agregar la pregunta...', de: 'Test auswählen, dem die Frage hinzugefügt werden soll...' },
  questionAdded: { en: 'Question will be added directly to the selected test', ar: 'سيتم إضافة السؤال مباشرة إلى الاختبار المحدد', sv: 'Frågan läggs direkt till i det valda testet', tr: 'Soru seçilen teste doğrudan eklenecek', fa: 'سوال مستقیماً به آزمون انتخابی اضافه می‌شود', so: "Su'aasha waxaa lagu dari doonaa imtixaanka la doortay", pl: 'Pytanie zostanie dodane bezpośrednio do wybranego testu', es: 'La pregunta se agregará directamente a la prueba seleccionada', de: 'Die Frage wird direkt zum ausgewählten Test hinzugefügt' },
  inputLanguage: { en: 'Input Language', ar: 'لغة الإدخال', sv: 'Inmatningsspråk', tr: 'Giriş Dili', fa: 'زبان ورودی', so: 'Luuqada gelinta', pl: 'Język wprowadzania', es: 'Idioma de entrada', de: 'Eingabesprache' },
  questionText: { en: 'Question Text *', ar: 'نص السؤال *', sv: 'Frågetext *', tr: 'Soru Metni *', fa: 'متن سوال *', so: "Qoraalka su'aasha *", pl: 'Treść pytania *', es: 'Texto de la pregunta *', de: 'Fragetext *' },
  questionPlaceholder: { en: 'Enter question here...', ar: 'أدخل السؤال هنا...', sv: 'Ange frågan här...', tr: 'Soruyu buraya girin...', fa: 'سوال را اینجا وارد کنید...', so: "Geli su'aasha halkan...", pl: 'Wprowadź pytanie tutaj...', es: 'Ingrese la pregunta aquí...', de: 'Frage hier eingeben...' },
  options: { en: 'Options *', ar: 'الخيارات *', sv: 'Alternativ *', tr: 'Seçenekler *', fa: 'گزینه‌ها *', so: 'Doorashooyinka *', pl: 'Opcje *', es: 'Opciones *', de: 'Optionen *' },
  optionPlaceholder: { en: 'Option', ar: 'الخيار', sv: 'Alternativ', tr: 'Seçenek', fa: 'گزینه', so: 'Doorasho', pl: 'Opcja', es: 'Opción', de: 'Option' },
  correctAnswer: { en: 'Click ✓ to set correct answer', ar: 'اضغط على ✓ لتحديد الإجابة الصحيحة', sv: 'Klicka ✓ för rätt svar', tr: 'Doğru cevabı seçmek için ✓ tıklayın', fa: 'برای تعیین پاسخ صحیح روی ✓ کلیک کنید', so: "Guji ✓ si aad u dejiso jawaabta saxda ah", pl: 'Kliknij ✓ aby ustawić poprawną odpowiedź', es: 'Haga clic en ✓ para establecer la respuesta correcta', de: 'Klicken Sie auf ✓ um die richtige Antwort festzulegen' },
  explanation: { en: 'Explanation *', ar: 'التفسير *', sv: 'Förklaring *', tr: 'Açıklama *', fa: 'توضیح *', so: 'Sharaxaad *', pl: 'Wyjaśnienie *', es: 'Explicación *', de: 'Erklärung *' },
  explanationPlaceholder: { en: 'Explain the correct answer...', ar: 'اشرح الإجابة الصحيحة...', sv: 'Förklara det rätta svaret...', tr: 'Doğru cevabı açıklayın...', fa: 'پاسخ صحیح را توضیح دهید...', so: 'Sharax jawaabta saxda ah...', pl: 'Wyjaśnij poprawną odpowiedź...', es: 'Explique la respuesta correcta...', de: 'Erklären Sie die richtige Antwort...' },
  image: { en: 'Question Image (Optional)', ar: 'صورة السؤال (اختياري)', sv: 'Frågebild (Valfritt)', tr: 'Soru Resmi (İsteğe bağlı)', fa: 'تصویر سوال (اختیاری)', so: "Sawirka su'aasha (Ikhtiyaari)", pl: 'Obraz pytania (Opcjonalnie)', es: 'Imagen de la pregunta (Opcional)', de: 'Fragebild (Optional)' },
  uploadImage: { en: 'Upload Image', ar: 'رفع صورة', sv: 'Ladda upp bild', tr: 'Resim Yükle', fa: 'آپلود تصویر', so: 'Soo geli sawir', pl: 'Prześlij obraz', es: 'Subir imagen', de: 'Bild hochladen' },
  cancel: { en: 'Cancel', ar: 'إلغاء', sv: 'Avbryt', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  saving: { en: 'Saving...', ar: 'جاري الحفظ...', sv: 'Sparar...', tr: 'Kaydediliyor...', fa: 'در حال ذخیره...', so: 'Keydinaya...', pl: 'Zapisywanie...', es: 'Guardando...', de: 'Speichern...' },
  saveTranslate: { en: 'Save & Translate', ar: 'حفظ وترجمة', sv: 'Spara & Översätt', tr: 'Kaydet ve Çevir', fa: 'ذخیره و ترجمه', so: 'Keydi & Turjum', pl: 'Zapisz i przetłumacz', es: 'Guardar y traducir', de: 'Speichern & Übersetzen' },
  questions: { en: 'questions', ar: 'سؤال', sv: 'frågor', tr: 'soru', fa: 'سوال', so: "su'aalood", pl: 'pytań', es: 'preguntas', de: 'Fragen' },
  // Validation messages
  enterQuestion: { en: 'Please enter the question', ar: 'يرجى إدخال السؤال', sv: 'Ange frågan', tr: 'Lütfen soruyu girin', fa: 'لطفاً سوال را وارد کنید', so: "Fadlan geli su'aasha", pl: 'Proszę wprowadzić pytanie', es: 'Por favor ingrese la pregunta', de: 'Bitte geben Sie die Frage ein' },
  enterAllOptions: { en: 'Please enter all options', ar: 'يرجى إدخال جميع الخيارات', sv: 'Ange alla alternativ', tr: 'Lütfen tüm seçenekleri girin', fa: 'لطفاً همه گزینه‌ها را وارد کنید', so: 'Fadlan geli dhammaan doorashooyinka', pl: 'Proszę wprowadzić wszystkie opcje', es: 'Por favor ingrese todas las opciones', de: 'Bitte geben Sie alle Optionen ein' },
  enterExplanation: { en: 'Please enter explanation', ar: 'يرجى إدخال التفسير', sv: 'Ange förklaring', tr: 'Lütfen açıklama girin', fa: 'لطفاً توضیح را وارد کنید', so: 'Fadlan geli sharaxaad', pl: 'Proszę wprowadzić wyjaśnienie', es: 'Por favor ingrese la explicación', de: 'Bitte geben Sie eine Erklärung ein' },
  selectTestMsg: { en: 'Please select a test to add the question to', ar: 'يرجى اختيار الاختبار الذي تريد إضافة السؤال إليه', sv: 'Välj testet att lägga till frågan i', tr: 'Lütfen soru eklenecek testi seçin', fa: 'لطفاً آزمونی را برای افزودن سوال انتخاب کنید', so: "Fadlan dooro imtixaanka aad su'aasha ku dari rabto", pl: 'Proszę wybrać test, do którego chcesz dodać pytanie', es: 'Por favor seleccione una prueba para agregar la pregunta', de: 'Bitte wählen Sie einen Test aus, um die Frage hinzuzufügen' },
  // Success messages
  addedSuccess: { en: 'Question added and translated! 🎉', ar: 'تم إضافة السؤال وترجمته بنجاح! 🎉', sv: 'Fråga tillagd och översatt! 🎉', tr: 'Soru eklendi ve çevrildi! 🎉', fa: 'سوال اضافه و ترجمه شد! 🎉', so: "Su'aasha waa lagu daray oo la turjumay! 🎉", pl: 'Pytanie dodane i przetłumaczone! 🎉', es: '¡Pregunta agregada y traducida! 🎉', de: 'Frage hinzugefügt und übersetzt! 🎉' },
  updatedSuccess: { en: 'Question updated and translated! 🎉', ar: 'تم تحديث السؤال وترجمته بنجاح! 🎉', sv: 'Fråga uppdaterad och översatt! 🎉', tr: 'Soru güncellendi ve çevrildi! 🎉', fa: 'سوال به‌روزرسانی و ترجمه شد! 🎉', so: "Su'aasha waa la cusbooneysiiyay oo la turjumay! 🎉", pl: 'Pytanie zaktualizowane i przetłumaczone! 🎉', es: '¡Pregunta actualizada y traducida! 🎉', de: 'Frage aktualisiert und übersetzt! 🎉' },
  addedToTestSuccess: { en: 'Question added to test and translated! 🎉', ar: 'تم إضافة السؤال للاختبار وترجمته بنجاح! 🎉', sv: 'Fråga tillagd i testet och översatt! 🎉', tr: 'Soru teste eklendi ve çevrildi! 🎉', fa: 'سوال به آزمون اضافه و ترجمه شد! 🎉', so: "Su'aasha waxaa lagu daray imtixaanka oo la turjumay! 🎉", pl: 'Pytanie dodane do testu i przetłumaczone! 🎉', es: '¡Pregunta agregada a la prueba y traducida! 🎉', de: 'Frage zum Test hinzugefügt und übersetzt! 🎉' },
  errorSaving: { en: 'Error saving question', ar: 'خطأ في حفظ السؤال', sv: 'Fel vid sparande', tr: 'Soru kaydedilirken hata', fa: 'خطا در ذخیره سوال', so: "Khalad keydinta su'aasha", pl: 'Błąd podczas zapisywania pytania', es: 'Error al guardar la pregunta', de: 'Fehler beim Speichern der Frage' },
  invalidFile: { en: 'Invalid file type. Use: JPG, PNG, GIF, WEBP', ar: 'صيغة الملف غير مدعومة. استخدم: JPG, PNG, GIF, WEBP', sv: 'Ogiltig filtyp. Använd: JPG, PNG, GIF, WEBP', tr: 'Geçersiz dosya türü. Kullanın: JPG, PNG, GIF, WEBP', fa: 'نوع فایل نامعتبر. استفاده کنید: JPG, PNG, GIF, WEBP', so: 'Nooca faylka saxdana maaha. Isticmaal: JPG, PNG, GIF, WEBP', pl: 'Nieprawidłowy typ pliku. Użyj: JPG, PNG, GIF, WEBP', es: 'Tipo de archivo inválido. Use: JPG, PNG, GIF, WEBP', de: 'Ungültiger Dateityp. Verwenden Sie: JPG, PNG, GIF, WEBP' },
  fileTooLarge: { en: 'File is too large. Max 10MB', ar: 'الملف كبير جداً. الحد الأقصى 10MB', sv: 'Filen är för stor. Max 10MB', tr: 'Dosya çok büyük. Maks 10MB', fa: 'فایل خیلی بزرگ است. حداکثر 10MB', so: 'Faylku aad buu u weyn yahay. Max 10MB', pl: 'Plik jest za duży. Maks. 10MB', es: 'El archivo es demasiado grande. Máx 10MB', de: 'Datei ist zu groß. Max 10MB' },
};

// Helper function to get translated text
const getText = (key, lang) => adminTexts[key]?.[lang] || adminTexts[key]?.en || key;

const QuestionModal = ({ open, onClose, question, token, onSuccess, i18n: propI18n, t, defaultLicenseType = 'b_korkort', lockLicenseType = false }) => {
  const { i18n: hookI18n } = useTranslation();
  const lang = hookI18n?.language || propI18n?.language || 'en';
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVideo, setPreviewVideo] = useState('');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const isSubmittingRef = useRef(false); // حماية فورية ضد الضغط المتكرر
  const [optionsCount, setOptionsCount] = useState(4); // عدد الخيارات (2-4)
  const [formData, setFormData] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    explanation: '',
    category: 'general',
    source_language: 'ar',
    image_url: '',
    video_url: '',
    disable_shuffle: false,  // خيار عدم خلط الإجابات
    // License type: 'b_korkort' (default) or 'taxi'. Taxi questions are Swedish-only.
    licenseType: 'b_korkort'
  });

  useEffect(() => {
    if (question) {
      // Edit mode - populate form with existing question data
      const imageUrl = question.image_url || '';
      
      // Map language to suffix for legacy fields
      const langSuffixMap = {
        'sv': 'Sv', 'en': 'En', 'ar': 'Ar',
        'tr': 'Tr', 'fa': 'Fa', 'so': 'So',
        'pl': 'Pl', 'es': 'Es', 'de': 'De'
      };
      const suffix = langSuffixMap[lang] || 'En';
      
      // Get question text - check both new and legacy formats
      let questionText = '';
      if (typeof question.question === 'object' && question.question !== null) {
        questionText = question.question[lang] || question.question.en || question.question.sv || '';
      }
      // Fallback to legacy format (questionSv, questionAr, etc.)
      if (!questionText) {
        questionText = question[`question${suffix}`] || question.questionSv || question.questionEn || '';
      }
      if (typeof question.question === 'string') {
        questionText = question.question;
      }
      
      // Get options in current language
      let optionsArr = ['', '', '', ''];
      if (typeof question.options === 'object' && question.options !== null) {
        if (Array.isArray(question.options)) {
          // Options are array of objects with language keys
          optionsArr = question.options.map(opt => {
            if (typeof opt === 'object') {
              return opt[lang] || opt[`text${suffix}`] || opt.sv || opt.en || opt.text || '';
            }
            return opt || '';
          });
        } else {
          // Options are language-keyed object
          optionsArr = question.options[lang] || question.options.sv || question.options.en || ['', '', '', ''];
        }
      }
      
      // Get explanation - check both new and legacy formats
      let explanationText = '';
      if (typeof question.explanation === 'object' && question.explanation !== null) {
        explanationText = question.explanation[lang] || question.explanation.en || question.explanation.sv || '';
      }
      // Fallback to legacy format
      if (!explanationText) {
        explanationText = question[`explanation${suffix}`] || question.explanationSv || question.explanationEn || '';
      }
      if (typeof question.explanation === 'string') {
        explanationText = question.explanation;
      }
      
      // Get correct answer - check both formats
      const correctAnswer = question.correct_answer ?? question.correctAnswer ?? 0;
      
      console.log('QuestionModal - Loading question:', {
        lang,
        questionText: questionText?.substring(0, 50),
        explanationText: explanationText?.substring(0, 50),
        optionsCount: optionsArr.filter(o => o).length
      });
      
      setFormData({
        question_text: questionText,
        options: optionsArr,
        correct_answer: correctAnswer,
        explanation: explanationText,
        category: question.category || 'general',
        source_language: lang,
        image_url: imageUrl,
        video_url: question.video_url || '',
        disable_shuffle: question.disable_shuffle || false,  // تحميل قيمة عدم الخلط
        licenseType: question.licenseType || 'b_korkort',
        taxiCategory: question.taxiCategory || null,
      });
      // تحديد عدد الخيارات بناءً على الخيارات الموجودة
      const validOptionsCount = optionsArr.filter(o => o && o.trim() !== '').length;
      setOptionsCount(Math.max(2, Math.min(4, validOptionsCount || 4)));
      
      // For preview, add backend URL if it's a relative path - only if image exists
      if (imageUrl && imageUrl.trim() !== '') {
        const previewUrl = imageUrl.startsWith('http') ? imageUrl : imageUrl.startsWith('/') ? imageUrl : `${API_URL}${imageUrl}`;
        setPreviewImage(previewUrl);
      } else {
        setPreviewImage('');
      }
      const videoUrl = question.video_url || '';
      if (videoUrl && videoUrl.trim() !== '') {
        const previewUrlV = videoUrl.startsWith('http') ? videoUrl : videoUrl.startsWith('/') ? videoUrl : `${API_URL}${videoUrl}`;
        setPreviewVideo(previewUrlV);
      } else {
        setPreviewVideo('');
      }
    } else {
      // Add mode - reset form. `defaultLicenseType` lets the parent pre-select
      // taxi when adding from the dedicated Taxifrågor tab.
      setFormData({
        question_text: '',
        options: ['', '', '', ''],
        correct_answer: 0,
        explanation: '',
        category: 'general',
        source_language: lang,
        image_url: '',
        video_url: '',
        disable_shuffle: false,  // قيمة افتراضية
        licenseType: defaultLicenseType || 'b_korkort',
        taxiCategory: defaultLicenseType === 'taxi' ? 'sakerhet_och_beteende' : null,
      });
      setOptionsCount(4); // افتراضياً 4 خيارات
      setPreviewImage('');
      setPreviewVideo('');
    }
  }, [question, open, lang, defaultLicenseType]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(lang === 'ar' 
        ? 'صيغة الملف غير مدعومة. استخدم: JPG, PNG, GIF, WEBP'
        : lang === 'sv'
        ? 'Ogiltig filtyp. Använd: JPG, PNG, GIF, WEBP'
        : 'Invalid file type. Use: JPG, PNG, GIF, WEBP'
      );
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(lang === 'ar' 
        ? 'حجم الملف يجب أن يكون أقل من 10MB'
        : lang === 'sv'
        ? 'Filstorleken måste vara mindre än 10MB'
        : 'File size must be less than 10MB'
      );
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/upload/image`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Store only relative path in database, not full URL
        const relativeUrl = response.data.url;
        const fullUrl = `${API_URL}${relativeUrl}`;
        setFormData({ ...formData, image_url: relativeUrl });
        setPreviewImage(fullUrl);
        toast.success(lang === 'ar' ? 'تم رفع الصورة! ✅' : lang === 'sv' ? 'Bild uppladdad! ✅' : 'Image uploaded! ✅');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(lang === 'ar' ? 'خطأ في رفع الصورة' : lang === 'sv' ? 'Fel vid uppladdning' : 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image_url: '' });
    setPreviewImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error(lang === 'ar'
        ? 'صيغة الفيديو غير مدعومة. استخدم: MP4, WEBM, MOV'
        : lang === 'sv'
        ? 'Ogiltig videotyp. Använd: MP4, WEBM, MOV'
        : 'Invalid video type. Use: MP4, WEBM, MOV'
      );
      return;
    }

    // No size cap — admin-only feature. Large files may take a while to
    // upload but the server accepts them.

    setUploadingVideo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(
        `${API_URL}/api/upload/video`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.success) {
        const relativeUrl = response.data.url;
        const fullUrl = `${API_URL}${relativeUrl}`;
        setFormData({ ...formData, video_url: relativeUrl });
        setPreviewVideo(fullUrl);
        toast.success(lang === 'ar' ? 'تم رفع الفيديو ✅' : lang === 'sv' ? 'Video uppladdad ✅' : 'Video uploaded ✅');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      const detail = error?.response?.data?.detail || '';
      toast.error(
        (lang === 'ar' ? 'خطأ في رفع الفيديو' : lang === 'sv' ? 'Fel vid uppladdning' : 'Error uploading video') +
        (detail ? `: ${detail}` : '')
      );
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = () => {
    setFormData({ ...formData, video_url: '' });
    setPreviewVideo('');
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // حماية فورية ضد الضغط المتكرر
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    
    // Validation
    if (!formData.question_text.trim()) {
      toast.error(getText('enterQuestion', lang));
      isSubmittingRef.current = false;
      return;
    }

    // التحقق فقط من الخيارات المحددة (حسب optionsCount)
    const selectedOptions = formData.options.slice(0, optionsCount);
    if (selectedOptions.some(opt => !opt.trim())) {
      toast.error(getText('enterAllOptions', lang));
      isSubmittingRef.current = false;
      return;
    }

    if (!formData.explanation.trim()) {
      toast.error(getText('enterExplanation', lang));
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    
    try {
      let endpoint, method, payload;
      
      if (question) {
        // Edit mode - use auto-translate to translate to ALL languages (same as adding new question)
        const questionId = question.id || question._id;
        console.log('Edit mode - Question ID:', questionId, 'Full question:', question);
        
        if (!questionId) {
          toast.error(lang === 'ar' ? 'خطأ: معرف السؤال غير موجود' : 'Error: Question ID not found');
          setLoading(false);
          return;
        }
        
        // Use auto-translate endpoint to update ALL languages automatically
        endpoint = `${API}/admin/questions/${questionId}/auto-translate`;
        method = 'put';
        // إرسال فقط الخيارات المحددة
        const validOptions = formData.options.slice(0, optionsCount).filter(o => o.trim() !== '');
        payload = {
          question_text: formData.question_text,
          options: validOptions,
          correct_answer: formData.correct_answer,
          explanation: formData.explanation,
          category: formData.category,
          source_language: formData.source_language,
          image_url: formData.image_url || '',
          video_url: formData.video_url || '',
          disable_shuffle: formData.disable_shuffle,  // إضافة خيار عدم الخلط
          licenseType: formData.licenseType || 'b_korkort',
          taxiCategory: formData.taxiCategory || null,
        };
        
        console.log('Sending payload for auto-translate:', payload);
      } else {
        // Add mode - use auto-translate
        endpoint = `${API}/admin/questions/auto-translate`;
        method = 'post';
        // إرسال فقط الخيارات المحددة
        const validOptions = formData.options.slice(0, optionsCount).filter(o => o.trim() !== '');
        payload = {
          ...formData,
          options: validOptions
        };
      }
      
      const response = await axios[method](endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(question ? getText('updatedSuccess', lang) : getText('addedSuccess', lang));
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error(getText('errorSaving', lang));
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Languages className="w-6 h-6 text-emerald-600" />
            {question ? getText('editQuestion', lang) : getText('addQuestion', lang)}
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-2">
            {getText('autoTranslate', lang)}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* License-type selector (b_korkort / taxi). For 'taxi' we force
              Swedish source language and skip auto-translate on the backend.
              Hidden entirely when `lockLicenseType` is set — that way the
              general "Questions" tab only creates B-körkort content, and the
              dedicated Taxifrågor tab only creates Taxi content. */}
          {!lockLicenseType && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-semibold whitespace-nowrap">
              {lang === 'ar' ? 'نوع الرخصة:' : 'License:'}
            </Label>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, licenseType: 'b_korkort' })}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                (formData.licenseType || 'b_korkort') === 'b_korkort'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
              data-testid="qmodal-license-b-korkort"
            >
              🚗 B-körkort
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, licenseType: 'taxi', source_language: 'sv' })}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                formData.licenseType === 'taxi'
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
              data-testid="qmodal-license-taxi"
            >
              🚕 Taxikort
            </button>
            {formData.licenseType === 'taxi' && (
              <span className="text-xs text-slate-500 italic">
                {lang === 'ar' ? 'السويدية فقط (بدون ترجمة)' : 'Swedish only (no translation)'}
              </span>
            )}
          </div>
          )}

          {/* Taxi sub-category selector (only for Taxi questions) */}
          {formData.licenseType === 'taxi' && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
              <Label className="font-semibold text-amber-900 block mb-2">
                {lang === 'ar' ? 'قسم Taxikort *' : 'Taxikort-område *'}
              </Label>
              <p className="text-xs text-amber-800 mb-3">
                {lang === 'ar'
                  ? 'اختر القسم الذي ينتمي إليه هذا السؤال — سيظهر للمستخدم فقط تحت القسم المختار.'
                  : 'Välj vilket område denna fråga tillhör.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { key: 'sakerhet_och_beteende', emoji: '🛡️', title: 'Säkerhet och beteende', ar: 'الأمان والسلوك' },
                  { key: 'lagstiftning', emoji: '⚖️', title: 'Lagstiftning', ar: 'التشريعات' },
                ].map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setFormData({ ...formData, taxiCategory: cat.key })}
                    data-testid={`qmodal-taxi-cat-${cat.key}`}
                    className={`p-3 rounded-lg text-sm font-semibold transition-all text-left rtl:text-right ${
                      formData.taxiCategory === cat.key
                        ? 'bg-amber-600 text-white shadow-md'
                        : 'bg-white border border-amber-300 text-amber-900 hover:bg-amber-100'
                    }`}
                  >
                    <div className="text-2xl mb-1">{cat.emoji}</div>
                    <div className="font-bold">{cat.title}</div>
                    <div className={`text-xs mt-0.5 ${formData.taxiCategory === cat.key ? 'text-amber-100' : 'text-amber-700'}`}>
                      {lang === 'ar' ? cat.ar : cat.title}
                    </div>
                  </button>
                ))}
              </div>
              {!formData.taxiCategory && (
                <p className="text-xs text-red-600 mt-2 font-semibold">
                  ⚠️ {lang === 'ar' ? 'يجب اختيار قسم قبل الحفظ' : 'Välj ett område innan du sparar'}
                </p>
              )}
            </div>
          )}

          {/* Language Selection */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <Label className="text-sm font-semibold text-amber-900 mb-2 block">
              {getText('inputLanguage', lang)}
            </Label>
            <Select
              value={formData.source_language}
              onValueChange={(val) => setFormData({ ...formData, source_language: val })}
              disabled={formData.licenseType === 'taxi'}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                <SelectItem value="sv">🇸🇪 Svenska</SelectItem>
                <SelectItem value="tr">🇹🇷 Türkçe</SelectItem>
                <SelectItem value="fa">🇮🇷 فارسی</SelectItem>
                <SelectItem value="so">🇸🇴 Soomaali</SelectItem>
                <SelectItem value="pl">🇵🇱 Polski</SelectItem>
                <SelectItem value="es">🇪🇸 Español</SelectItem>
                <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Question Text */}
          <div>
            <Label className="text-sm font-semibold">
              {getText('questionText', lang)}
            </Label>
            <Textarea
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              placeholder={getText('questionPlaceholder', lang)}
              className="mt-2"
              rows={3}
              required
            />
          </div>

          {/* Options */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">
                {getText('options', lang)}
              </Label>
              {/* اختيار عدد الخيارات */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {lang === 'ar' ? 'عدد الخيارات:' : lang === 'sv' ? 'Antal alternativ:' : 'Options count:'}
                </span>
                <div className="flex gap-1">
                  {[2, 3, 4].map(num => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => {
                        setOptionsCount(num);
                        // تحديث الخيارات
                        const newOptions = [...formData.options];
                        while (newOptions.length < num) newOptions.push('');
                        setFormData({ 
                          ...formData, 
                          options: newOptions.slice(0, 4),
                          correct_answer: formData.correct_answer >= num ? 0 : formData.correct_answer
                        });
                      }}
                      className={`w-8 h-8 rounded-full text-sm font-bold transition-all ${
                        optionsCount === num 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {formData.options.slice(0, optionsCount).map((option, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    formData.correct_answer === index 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`${getText('optionPlaceholder', lang)} ${index + 1}`}
                    className="flex-1"
                    required
                  />
                  <Button
                    type="button"
                    variant={formData.correct_answer === index ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData({ ...formData, correct_answer: index })}
                    className={formData.correct_answer === index ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                  >
                    {formData.correct_answer === index ? '✓' : ' '}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {getText('correctAnswer', lang)}
            </p>
          </div>

          {/* Explanation */}
          <div>
            <Label className="text-sm font-semibold">
              {getText('explanation', lang)}
            </Label>
            <Textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              placeholder={getText('explanationPlaceholder', lang)}
              className="mt-2"
              rows={3}
              required
            />
          </div>

          {/* Image Upload (Optional) */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">
              {getText('image', lang)}
            </Label>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {getText('saving', lang)}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {getText('uploadImage', lang)}
                  </>
                )}
              </Button>
              
              {previewImage && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveImage}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 me-1" />
                  {getText('cancel', lang)}
                </Button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileUpload}
              className="hidden"
            />

            {previewImage && (
              <div className="mt-3 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-slate-600">
                    {getText('image', lang).replace('*', '').replace('(Optional)', '')}:
                  </span>
                </div>
                <img 
                  src={previewImage} 
                  alt="Preview" 
                  className="mt-3 max-w-full h-auto max-h-48 rounded-lg object-cover"
                />
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-slate-500">
                  {lang === 'ar' ? 'أو' : lang === 'sv' ? 'ELLER' : 'OR'}
                </span>
              </div>
            </div>

            <Input
              type="text"
              value={formData.image_url}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, image_url: value });
                // For preview, add backend URL if it's a relative path
                const preview = value.startsWith('http') ? value : `${API_URL}${value}`;
                setPreviewImage(preview);
              }}
              placeholder={getText('questionPlaceholder', lang)}
            />
          </div>

          {/* Video Upload (Optional) — stored in MongoDB GridFS, same
              cross-environment strategy as images. */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">
              {lang === 'ar' ? '🎬 فيديو (اختياري)' : lang === 'sv' ? '🎬 Video (valfritt)' : '🎬 Video (Optional)'}
            </Label>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                className="flex items-center gap-2"
                data-testid="question-upload-video-btn"
              >
                {uploadingVideo ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {getText('saving', lang)}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {lang === 'ar' ? 'رفع فيديو' : lang === 'sv' ? 'Ladda upp video' : 'Upload Video'}
                  </>
                )}
              </Button>

              {previewVideo && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveVideo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="question-remove-video-btn"
                >
                  <X className="w-4 h-4 me-1" />
                  {getText('cancel', lang)}
                </Button>
              )}
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoUpload}
              className="hidden"
              data-testid="question-video-file-input"
            />

            <p className="text-xs text-slate-500">
              {lang === 'ar'
                ? 'MP4 / WEBM / MOV'
                : 'MP4 / WEBM / MOV'}
            </p>

            {previewVideo && (
              <div className="mt-3 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <VideoIcon className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-slate-600">
                    {lang === 'ar' ? 'معاينة الفيديو:' : lang === 'sv' ? 'Video-förhandsvisning:' : 'Video preview:'}
                  </span>
                </div>
                <video
                  src={previewVideo}
                  controls
                  className="max-w-full h-auto max-h-64 rounded-lg"
                  data-testid="question-video-preview"
                />
                <a
                  href={previewVideo}
                  download={(formData.video_url || '').split('/').pop() || 'video.mp4'}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-4 py-2 text-sm font-semibold text-white"
                  data-testid="question-download-video-btn"
                >
                  <Download className="w-4 h-4" />
                  {lang === 'ar' ? 'تحميل الفيديو' : lang === 'sv' ? 'Ladda ner video' : 'Download video'}
                </a>
              </div>
            )}
          </div>

          {/* خيار عدم خلط الإجابات */}
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="disable_shuffle"
              checked={formData.disable_shuffle}
              onChange={(e) => setFormData({ ...formData, disable_shuffle: e.target.checked })}
              className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="disable_shuffle" className="flex-1 cursor-pointer">
              <span className="font-medium text-amber-800">
                {lang === 'ar' ? '🔒 عدم خلط الإجابات' : lang === 'sv' ? '🔒 Blanda inte svaren' : '🔒 Don\'t shuffle answers'}
              </span>
              <p className="text-sm text-amber-600 mt-1">
                {lang === 'ar' 
                  ? 'فعّل هذا الخيار إذا كانت الإجابات مرتبطة بالصورة (مثل: A, B, C, D)'
                  : lang === 'sv'
                  ? 'Aktivera om svaren är kopplade till bilden (t.ex. A, B, C, D)'
                  : 'Enable if answers are linked to the image (e.g., A, B, C, D)'}
              </p>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {getText('cancel', lang)}
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {getText('saving', lang)}
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  {getText('saveTranslate', lang)}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionModal;
